import React, { useEffect, useState } from "react";
import groupBy from "lodash/groupBy";
import sumBy from "lodash/sumBy";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";
import Link from "@material-ui/core/Link";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Divider from "@material-ui/core/Divider";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  pageTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: "#333",
  },
  statsContainer: {
    marginBottom: theme.spacing(3),
  },
  statsCard: {
    padding: theme.spacing(1.5),
    textAlign: "center",
    border: "1px solid #e0e0e0",
    borderRadius: 4,
  },
  statsValue: {
    fontSize: "1.75rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
    [theme.breakpoints.down('xs')]: {
      fontSize: "1.25rem",
    },
  },
  statsLabel: {
    fontSize: "0.75rem",
    color: "#666",
    textTransform: "uppercase",
    [theme.breakpoints.down('xs')]: {
      fontSize: "0.65rem",
    },
  },
  projectCard: {
    marginBottom: theme.spacing(2),
    border: "1px solid #e0e0e0",
    borderRadius: 4,
  },
  projectHeader: {
    padding: theme.spacing(1.5),
    backgroundColor: "#fafafa",
    borderBottom: "1px solid #e0e0e0",
  },
  projectName: {
    fontSize: "1rem",
    fontWeight: 600,
    color: "#333",
  },
  projectPrice: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
    [theme.breakpoints.down('xs')]: {
      fontSize: "0.9rem",
    },
  },
  resourceSection: {
    padding: theme.spacing(2),
  },
  sectionTitle: {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: "#666",
    marginBottom: theme.spacing(1),
    textTransform: "uppercase",
  },
  table: {
    "& .MuiTableCell-root": {
      padding: "8px 12px",
      fontSize: "0.875rem",
    },
    "& .MuiTableCell-head": {
      fontWeight: 600,
      backgroundColor: "#f5f5f5",
      color: "#666",
    },
  },
  resourceName: {
    fontWeight: 500,
    color: "#333",
  },
  label: {
    fontSize: "0.75rem",
    color: "#999",
    marginRight: theme.spacing(0.5),
  },
  value: {
    fontSize: "0.875rem",
    color: "#333",
  },
  statusChip: {
    height: 20,
    fontSize: "0.7rem",
  },
  linkButton: {
    fontSize: "0.875rem",
    color: theme.palette.primary.main,
  },
  compactRow: {
    "&:last-child td": {
      borderBottom: 0,
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "60vh",
  },
  errorContainer: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: "#d32f2f",
  },
  emptyState: {
    padding: theme.spacing(2),
    color: "#999",
    fontStyle: "italic",
    fontSize: "0.875rem",
  },
  searchBox: {
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      backgroundColor: "white",
    },
  },
}));

const DigitalOcean = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8080/";

  const [totalByProject, setTotalByProject] = useState([]);
  const [totalProjects, setTotalProjects] = useState(0);
  const [totalResources, setTotalResources] = useState(0);
  const [projectsTotal, setProjectsTotal] = useState(0);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [nextRun, setNextRun] = useState(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_URL}digitalocean/inventory`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          // Handle rate limiting specifically
          if (response.status === 429) {
            const data = await response.json();
            throw new Error(data.message || "Digital Ocean API rate limit exceeded. Please try again in a few minutes.");
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        // Transform DB inventory into the structure expected by the UI
        const byProject = groupBy(data.resources || [], 'project_id');
        const transformed = (data.projects || []).map(p => ({
          id: p.project_id,
          name: p.name,
          resources: (byProject[p.project_id] || []).map(r => ({
            type: r.resource_type,
            id: r.resource_id,
            details: {
              name: r.name,
              status: r.status,
              region: r.region,
              ip_address: r.ip_address,
              vcpus: r.vcpus,
              memory: r.memory_mb,
              disk: r.disk_gb,
              price_monthly: r.price_monthly,
              tier_slug: r.tier_slug,
              live_url: null,
              backups_enabled: r.backups_enabled === 1
            }
          }))
        }));
        setProjects(transformed);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchProjects();
  }, [API_URL]);

  // Load latest DigitalOcean refresh timestamp AND next scheduled run
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const token = localStorage.getItem("token");
        // Fetch sync log (for last refresh)
        const resLog = await fetch(`${API_URL}api/maintenance/digitalocean/sync-logs?limit=1`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        if (resLog.ok) {
          const data = await resLog.json();
          const log = data?.logs?.[0];
          if (log?.completed_at) setLastRefresh(new Date(log.completed_at));
          else if (log?.started_at) setLastRefresh(new Date(log.started_at));
        }
        // Fetch status (for next run)
        const resStatus = await fetch(`${API_URL}api/maintenance/digitalocean/status`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        if (resStatus.ok) {
          const status = await resStatus.json();
          if (status?.nextRun) setNextRun(new Date(status.nextRun));
        }
      } catch (_) {}
    };
    fetchStatus();
  }, [API_URL]);

  const getTotalByProject = (id) => {
    const result = totalByProject.find((p) => p.id === id);
    return result ? result.price : 0;
  };

  const formatPrice = (price) => {
    // Always round to nearest dollar for cleaner KPI display
    return Math.round(price);
  };

  const groupResourcesByType = (resources) => {
    return groupBy(resources, "type");
  };

  // Filter projects and resources based on search term
  const filteredProjects = projects.filter((project) => {
    const searchLower = searchTerm.toLowerCase();
    
    // Check if project name matches
    if (project.name.toLowerCase().includes(searchLower)) {
      return true;
    }
    
    // Check if any resource name matches
    return project.resources.some((resource) => {
      const resourceName = resource.details?.name || resource.id;
      return resourceName.toLowerCase().includes(searchLower);
    });
  }).map((project) => {
    // If searching, filter resources within the project too
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const filteredResources = project.resources.filter((resource) => {
        const resourceName = resource.details?.name || resource.id;
        return resourceName.toLowerCase().includes(searchLower) || 
               project.name.toLowerCase().includes(searchLower);
      });
      
      return {
        ...project,
        resources: filteredResources,
      };
    }
    return project;
  });

  useEffect(() => {
    if (projects.length > 0) {
      let prices = [];
      let resourceCount = 0;

      for (let p = 0; p < projects.length; p++) {
        let projectID = projects[p].id;
        resourceCount += projects[p].resources.length;

        for (let r = 0; r < projects[p].resources.length; r++) {
          const resource = projects[p].resources[r];
          // Include both droplet and app pricing
          if (resource.type === "droplet" && resource.details?.price_monthly) {
            prices.push({
              id: projectID,
              price: resource.details.price_monthly,
            });
          } else if (resource.type === "app" && resource.details?.price_monthly) {
            prices.push({
              id: projectID,
              price: resource.details.price_monthly,
            });
          }
        }
      }

      const projects_total = sumBy(prices, "price");
      const grouped = groupBy(prices, "id");
      const totalGroupedByProject = Object.keys(grouped).map((key) => ({
        id: key,
        price: sumBy(grouped[key], "price"),
      }));

      setTotalByProject(totalGroupedByProject);
      setProjectsTotal(projects_total);
      setTotalProjects(projects.length);
      setTotalResources(resourceCount);
    }
  }, [projects]);

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.errorContainer}>
        <Typography variant="h6">Error loading data</Typography>
        <Typography variant="body2">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Grid container alignItems="center" justifyContent="space-between" style={{ marginBottom: 16 }}>
        <Grid item>
          <Typography className={classes.pageTitle}>
            DigitalOcean Infrastructure
          </Typography>
        </Grid>
        <Grid item>
          <Box display="flex" alignItems="center">
            <Button
              variant="outlined"
              color="primary"
              disabled={refreshing}
              onClick={async () => {
                try {
                  setRefreshing(true);
                  const token = localStorage.getItem("token");
                  await fetch(`${API_URL}api/maintenance/digitalocean/refresh-reconcile`, {
                    method: 'POST',
                    headers: {
                      Authorization: `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    }
                  });
                  // Re-fetch projects after refresh
                  const resp = await fetch(`${API_URL}digitalocean/inventory`, {
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
                  });
                  if (resp.ok) {
                    const data = await resp.json();
                    const byProject = groupBy(data.resources || [], 'project_id');
                    const transformed = (data.projects || []).map(p => ({
                      id: p.project_id,
                      name: p.name,
                      resources: (byProject[p.project_id] || []).map(r => ({
                        type: r.resource_type,
                        id: r.resource_id,
                        details: {
                          name: r.name,
                          status: r.status,
                          region: r.region,
                          ip_address: r.ip_address,
                          vcpus: r.vcpus,
                          memory: r.memory_mb,
                          disk: r.disk_gb,
                          price_monthly: r.price_monthly,
                          tier_slug: r.tier_slug,
                          live_url: null,
                          backups_enabled: r.backups_enabled === 1
                        }
                      }))
                    }));
                    setProjects(transformed);
                  }
                  // Update last refresh marker
                  const res = await fetch(`${API_URL}api/maintenance/digitalocean/sync-logs?limit=1`, {
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
                  });
                  if (res.ok) {
                    const logs = await res.json();
                    const log = logs?.logs?.[0];
                    if (log?.completed_at) setLastRefresh(new Date(log.completed_at));
                    else if (log?.started_at) setLastRefresh(new Date(log.started_at));
                  }
                } catch (_) {}
                finally {
                  setRefreshing(false);
                }
              }}
              style={{ marginRight: 12 }}
            >
              {refreshing ? 'Refreshing…' : 'Refresh & Reconcile'}
            </Button>
            <Box display="flex" flexDirection="column">
              {lastRefresh && (
                <Typography variant="caption" style={{ color: '#666' }}>
                  Last refresh: {lastRefresh.toLocaleString()}
                </Typography>
              )}
              {nextRun && (
                <Typography variant="caption" style={{ color: '#666' }}>
                  Next run: {nextRun.toLocaleString()}
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Search Box */}
      <TextField
        className={classes.searchBox}
        fullWidth
        variant="outlined"
        size="small"
        placeholder="Search by project or resource name..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* Compact Stats */}
      <Grid container spacing={2} className={classes.statsContainer}>
        <Grid item xs={4}>
          <Paper className={classes.statsCard}>
            <Typography className={classes.statsValue}>{totalProjects}</Typography>
            <Typography className={classes.statsLabel}>Projects</Typography>
          </Paper>
        </Grid>
        <Grid item xs={4}>
          <Paper className={classes.statsCard}>
            <Typography className={classes.statsValue}>{totalResources}</Typography>
            <Typography className={classes.statsLabel}>Resources</Typography>
          </Paper>
        </Grid>
        <Grid item xs={4}>
          <Paper className={classes.statsCard}>
            <Typography className={classes.statsValue}>
              ${formatPrice(projectsTotal)}
            </Typography>
            <Typography className={classes.statsLabel}>Monthly</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Removed inline refresh button; controls moved next to title */}

      {/* Projects List */}
      {filteredProjects.map((project, index) => {
        const groupedResources = groupResourcesByType(project.resources);
        
        return (
          <Paper key={index} className={classes.projectCard}>
            {/* Compact Project Header */}
            <Box className={classes.projectHeader}>
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                  <Typography className={classes.projectName}>
                    {project.name}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography className={classes.projectPrice}>
                    ${formatPrice(getTotalByProject(project.id))}/mo
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Grouped Resources */}
            <Box className={classes.resourceSection}>
              {/* Droplets */}
              {groupedResources.droplet && groupedResources.droplet.length > 0 && (
                <Box mb={2}>
                  <Typography className={classes.sectionTitle}>
                    Droplets ({groupedResources.droplet.length})
                  </Typography>
                  <TableContainer>
                    <Table size="small" className={classes.table}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell align="center">IP Address</TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell align="center">Backup</TableCell>
                          <TableCell align="center">Specs</TableCell>
                          <TableCell align="right">Price</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {groupedResources.droplet.map((resource, idx) => (
                          <TableRow key={idx} className={classes.compactRow}>
                            <TableCell>
                              <Typography className={classes.resourceName}>
                                {resource.details?.name || resource.id}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              {resource.details?.ip_address ? (
                                <Link
                                  href={`http://${resource.details.ip_address}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className={classes.linkButton}
                                >
                                  {resource.details.ip_address}
                                </Link>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={resource.details?.status || "active"}
                                size="small"
                                color={resource.details?.status === "active" ? "primary" : "default"}
                                className={classes.statusChip}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {resource.details?.backups_enabled ? (
                                <CheckCircleIcon style={{ color: "#4caf50", fontSize: 20 }} />
                              ) : (
                                <CancelIcon style={{ color: "#f44336", fontSize: 20 }} />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="caption">
                                {resource.details?.vcpus}vCPU / {resource.details?.memory}MB / {resource.details?.disk}GB
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" style={{ fontWeight: 600 }}>
                                ${resource.details?.price_monthly || 0}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Apps */}
              {groupedResources.app && groupedResources.app.length > 0 && (
                <Box mb={2}>
                  <Typography className={classes.sectionTitle}>
                    Applications ({groupedResources.app.length})
                  </Typography>
                  <TableContainer>
                    <Table size="small" className={classes.table}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell align="center">Region</TableCell>
                          <TableCell align="center">Tier</TableCell>
                          <TableCell align="center">URL</TableCell>
                          <TableCell align="right">Price</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {groupedResources.app.map((resource, idx) => (
                          <TableRow key={idx} className={classes.compactRow}>
                            <TableCell>
                              <Typography className={classes.resourceName}>
                                {resource.details?.name || resource.id}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              {resource.details?.region || "-"}
                            </TableCell>
                            <TableCell align="center">
                              {resource.details?.tier_slug || "-"}
                            </TableCell>
                            <TableCell align="center">
                              {resource.details?.live_url ? (
                                <Link
                                  href={resource.details.live_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className={classes.linkButton}
                                >
                                  View App
                                </Link>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" style={{ fontWeight: 600 }}>
                                ${resource.details?.price_monthly || 0}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Databases */}
              {groupedResources.database && groupedResources.database.length > 0 && (
                <Box mb={2}>
                  <Typography className={classes.sectionTitle}>
                    Databases ({groupedResources.database.length})
                  </Typography>
                  <TableContainer>
                    <Table size="small" className={classes.table}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell align="center">Engine</TableCell>
                          <TableCell align="center">Version</TableCell>
                          <TableCell align="center">Nodes</TableCell>
                          <TableCell align="center">Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {groupedResources.database.map((resource, idx) => (
                          <TableRow key={idx} className={classes.compactRow}>
                            <TableCell>
                              <Typography className={classes.resourceName}>
                                {resource.details?.name || resource.id}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">{resource.details?.engine}</TableCell>
                            <TableCell align="center">{resource.details?.version}</TableCell>
                            <TableCell align="center">{resource.details?.num_nodes}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={resource.details?.status || "online"}
                                size="small"
                                color="primary"
                                className={classes.statusChip}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Other Resources */}
              {["space", "domain", "volume", "kubernetes", "load_balancer"].map((type) => {
                const resources = groupedResources[type];
                if (!resources || resources.length === 0) return null;

                return (
                  <Box mb={2} key={type}>
                    <Typography className={classes.sectionTitle}>
                      {type.replace("_", " ").toUpperCase()}S ({resources.length})
                    </Typography>
                    <TableContainer>
                      <Table size="small" className={classes.table}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell align="right">Info</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {resources.map((resource, idx) => (
                            <TableRow key={idx} className={classes.compactRow}>
                              <TableCell>
                                <Typography className={classes.resourceName}>
                                  {resource.details?.name || resource.id}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="caption">
                                  {type === "space" && resource.details?.endpoint}
                                  {type === "domain" && resource.details?.name}
                                  {type === "volume" && `${resource.details?.size_gigabytes}GB`}
                                  {type === "kubernetes" && resource.details?.version}
                                  {type === "load_balancer" && resource.details?.ip}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
};

export default DigitalOcean;
